import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { flagFields } from '../lib/profanity'
import ReportModal from '../components/ReportModal'
import AcknowledgeModal from '../components/AcknowledgeModal'

const SEED_POSTS = [
  { id:'b1', title:'Welcome to the Wind Sports Directory', body:'We\'re excited to launch the first community-powered global directory for windsurfing, kiteboarding, wingfoil, and windfoil clubs and schools. Our mission is to connect wind sports enthusiasts worldwide...', author_name:'Admin', is_admin_post:true, created_at: new Date(Date.now()-86400000*2).toISOString(), category:'announcement' },
  { id:'b2', title:'Best Winter Kite Spots — Community Picks 2025', body:'The community has spoken! Here are the top-voted winter kite destinations based on our directory votes. Dakhla, Morocco tops the list for the third year running with its year-round lagoon conditions and consistently strong winds...', author_name:'WindJunkie_88', is_admin_post:false, created_at: new Date(Date.now()-86400000).toISOString(), category:'guide' },
  { id:'b3', title:'New: Wingfoil Listings Now Available', body:'We\'ve added Wingfoil as a full discipline category across the directory. If you run or know of a wingfoil school or club, submit it through our new form. The discipline is growing fast and we want to be the go-to resource...', author_name:'Admin', is_admin_post:true, created_at: new Date(Date.now()-86400000*5).toISOString(), category:'update' },
]

export default function BlogPage() {
  const { user, role } = useAuth()
  const [posts, setPosts]       = useState(SEED_POSTS)
  const [tab, setTab]           = useState('all')   // all | admin | community
  const [showForm, setShowForm] = useState(false)
  const [form, setForm]         = useState({ title:'', body:'', category:'story', location_label:'', location_city:'', location_country:'' })
  const [loading, setLoading]   = useState(false)
  const [msg, setMsg]           = useState('')
  const [showAck, setShowAck]       = useState(false)
  const [reporting, setReporting]   = useState(null)
  const [locationSearch, setLocationSearch] = useState('')
  const [expandedPost, setExpandedPost]     = useState(null)  // post id with comments open
  const [comments, setComments]             = useState({})    // { postId: [comment] }
  const [newComment, setNewComment]         = useState('')
  const [commentLoading, setCommentLoading] = useState(false)  // { id, name } of post being reported

  useEffect(() => { fetchPosts() }, [])

  async function fetchPosts() {
    const { data } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('status','approved')
      .order('created_at', { ascending:false })
    if (data && data.length) setPosts(data)
  }

  function handleWriteSubmit(e) {
    e.preventDefault()
    if (!user) { setMsg('Please log in to post.'); return }
    setShowAck(true)
  }

  async function handleConfirmedPost() {
    setShowAck(false)
    const flagged = flagFields(form)
    setLoading(true)
    await supabase.from('blog_posts').insert({
      ...form,
      author_id:   user.id,
      author_name: user.user_metadata?.username || user.email?.split('@')[0],
      is_admin_post: role >= 200,
      status: flagged ? 'flagged' : 'pending',
      location_label: form.location_label || null,
      location_city:  form.location_city  || null,
      location_country: form.location_country || null,
    })

    // Email notification
    try {
      await supabase.functions.invoke('notify-submission', {
        body: { type: 'blog_post', name: form.title, flagged }
      })
    } catch (_) {}

    setLoading(false)
    setMsg(flagged ? 'Post submitted but flagged for review.' : 'Post published! It\'s live and our team will review it shortly.')
    setShowForm(false)
    setForm({ title:'', body:'', category:'story', location_label:'', location_city:'', location_country:'' })
    fetchPosts()
  }

  const filtered = posts.filter(p => {
    if (tab === 'admin')     return p.is_admin_post
    if (tab === 'community') return !p.is_admin_post
    return true
  })

  const CATS = { announcement:'📢', guide:'📖', story:'🌊', update:'🔔', tips:'💡' }

  return (
    <div>
      <Navbar />
      <div className="page-wrap" style={{ maxWidth:720 }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12, marginBottom:24 }}>
          <div>
            <div className="page-title">Blog</div>
            <div className="page-sub" style={{ marginBottom:0 }}>News from the team + community stories</div>
          </div>
          {user && (
            <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
              {showForm ? 'Cancel' : '✏️ Write a Post'}
            </button>
          )}
          {!user && (
            <p style={{ fontSize:12, color:'var(--gray-400)', fontStyle:'italic' }}>Log in to write a community post</p>
          )}
        </div>

        {msg && (
          <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 }}>{msg}</div>
        )}

        {/* Write form */}
        {showForm && (
          <div style={{ background:'#fff', border:'1.5px solid var(--gray-200)', borderRadius:12, padding:24, marginBottom:24 }}>
            <div style={{ fontWeight:700, marginBottom:16 }}>New Post</div>
            <form onSubmit={handleWriteSubmit}>
              <div className="field">
                <label>Title *</label>
                <input value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} required placeholder="What's your post about?" />
              </div>
              <div className="field">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm(f=>({...f,category:e.target.value}))}>
                  {Object.entries(CATS).map(([k,v]) => <option key={k} value={k}>{v} {k.charAt(0).toUpperCase()+k.slice(1)}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Location <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(optional — for spot-specific posts)</span></label>
                <input
                  value={form.location_label}
                  onChange={e => setForm(f=>({...f, location_label:e.target.value}))}
                  placeholder="e.g. Cocoa, FL  ·  Hood River, OR  ·  Tarifa, Spain"
                />
                <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:3 }}>
                  📍 Lets readers filter posts by spot. Leave blank for general posts.
                </div>
              </div>
              <div className="field">
                <label>Content *</label>
                <textarea value={form.body} onChange={e => setForm(f=>({...f,body:e.target.value}))} required placeholder="Write your post…" style={{ minHeight:160 }} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Publishing…':'Publish Post'}</button>
            </form>
          </div>
        )}

        {/* Tab filter + location search */}
        <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ display:'flex', gap:6 }}>
            {[['all','All Posts'],['admin','📰 Official'],['community','🌊 Community']].map(([v,l]) => (
            <button key={v} onClick={() => setTab(v)}
              style={{ fontSize:12, fontWeight:500, padding:'5px 14px', borderRadius:50, border:`1.5px solid ${tab===v?'var(--blue)':'var(--gray-200)'}`, background:tab===v?'var(--blue)':'#fff', color:tab===v?'#fff':'var(--gray-500)', cursor:'pointer', transition:'all .15s' }}>
              {l}
            </button>
          ))}
          </div>
          {/* Location search */}
          <div style={{ display:'flex', alignItems:'center', gap:8, background:'#fff', border:'1.5px solid var(--gray-200)', borderRadius:8, padding:'5px 12px', marginLeft:'auto' }}>
            <span style={{ fontSize:13 }}>📍</span>
            <input
              value={locationSearch}
              onChange={e => setLocationSearch(e.target.value)}
              placeholder="Filter by location…"
              style={{ border:'none', outline:'none', fontSize:13, color:'var(--gray-700)', background:'none', width:150, fontFamily:'var(--font)' }}
            />
            {locationSearch && <button onClick={() => setLocationSearch('')} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--gray-400)', fontSize:16, lineHeight:1, padding:0 }}>×</button>}
          </div>
        </div>

        {/* Posts */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {filtered
            .filter(p => !locationSearch || (p.location_label || '').toLowerCase().includes(locationSearch.toLowerCase()))
            .map(p => (
            <div key={p.id} style={{ background:'#fff', border:`1.5px solid ${p.is_admin_post?'var(--blue)':'var(--gray-200)'}`, borderRadius:12, padding:24, position:'relative' }}>
              {p.is_admin_post && (
                <div style={{ position:'absolute', top:14, right:14, background:'var(--blue)', color:'#fff', fontFamily:'var(--mono)', fontSize:9, fontWeight:700, letterSpacing:1, textTransform:'uppercase', padding:'2px 7px', borderRadius:4 }}>
                  Official
                </div>
              )}
              <div style={{ fontFamily:'var(--mono)', fontSize:10, color:'var(--gray-400)', marginBottom:8, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <span>{CATS[p.category] || '📝'} {p.category?.toUpperCase()} · {new Date(p.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</span>
                {p.location_label && (
                  <span style={{ background:'var(--gray-100)', color:'var(--gray-500)', padding:'2px 7px', borderRadius:4, fontSize:10, fontWeight:600 }}>
                    📍 {p.location_label}
                  </span>
                )}
              </div>
              <div style={{ fontSize:18, fontWeight:700, marginBottom:8, lineHeight:1.2, paddingRight:p.is_admin_post?80:0 }}>
                {p.title}
              </div>
              <p style={{ fontSize:13, color:'var(--gray-500)', lineHeight:1.65, marginBottom:12 }}>
                {p.body.length > 300 ? p.body.slice(0,300)+'…' : p.body}
              </p>
              <div style={{ fontSize:12, color:'var(--gray-400)', display:'flex', alignItems:'center', gap:8 }}>
                <span>By <strong style={{ color:'var(--gray-600)' }}>{p.author_name}</strong></span>
                {/* Comments toggle */}
                <button
                  onClick={() => toggleComments(p.id)}
                  style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color: expandedPost===p.id ? 'var(--blue)' : 'var(--gray-400)', fontFamily:'var(--font)', display:'flex', alignItems:'center', gap:4 }}
                >
                  💬 {comments[p.id]?.length ?? ''} Comments
                </button>
                <button
                  onClick={() => setReporting({ id: p.id, name: p.title })}
                  title="Report this post"
                  style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'var(--gray-300)', fontSize:13, padding:'2px 4px', borderRadius:4, transition:'color .15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--gray-300)'}
                >🚩</button>
              </div>

              {/* Comments section */}
              {expandedPost === p.id && (
                <div style={{ marginTop:16, borderTop:'1px solid var(--gray-100)', paddingTop:16 }}>
                  {/* Existing comments */}
                  <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
                    {(comments[p.id] || []).length === 0 && (
                      <p style={{ fontSize:12, color:'var(--gray-400)', fontStyle:'italic' }}>No comments yet — be the first!</p>
                    )}
                    {(comments[p.id] || []).map(cm => (
                      <div key={cm.id} style={{ background:'var(--gray-50)', borderRadius:8, padding:'10px 12px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                          <strong style={{ fontSize:12, color:'var(--gray-700)' }}>{cm.author_name}</strong>
                          <span style={{ fontSize:11, color:'var(--gray-400)', fontFamily:'var(--mono)' }}>
                            {new Date(cm.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                          </span>
                          {(user && (cm.author_id === user.id)) && (
                            <button onClick={() => deleteComment(p.id, cm.id)} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'var(--gray-300)', fontSize:12 }}>🗑</button>
                          )}
                          {role >= 100 && cm.author_id !== user?.id && (
                            <button onClick={() => deleteComment(p.id, cm.id)} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'var(--gray-300)', fontSize:12 }}>🗑</button>
                          )}
                        </div>
                        <p style={{ fontSize:13, color:'var(--gray-600)', lineHeight:1.55, margin:0 }}>{cm.body}</p>
                      </div>
                    ))}
                  </div>
                  {/* New comment input */}
                  <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                    <textarea
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      placeholder={user ? 'Add a comment…' : 'Login to comment'}
                      disabled={!user}
                      style={{ flex:1, border:'1.5px solid var(--gray-200)', borderRadius:8, padding:'8px 12px', fontSize:13, fontFamily:'var(--font)', resize:'none', minHeight:60, outline:'none' }}
                      onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                      onBlur={e => e.target.style.borderColor = 'var(--gray-200)'}
                    />
                    <button
                      onClick={() => submitComment(p.id)}
                      disabled={!user || !newComment.trim() || commentLoading}
                      style={{ background:'var(--blue)', color:'#fff', border:'none', borderRadius:8, padding:'8px 14px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'var(--font)', whiteSpace:'nowrap', opacity: !user||!newComment.trim() ? .5 : 1 }}
                    >
                      Post
                    </button>
                  </div>
                  {!user && <p style={{ fontSize:11, color:'var(--gray-400)', marginTop:6 }}>
                    <button onClick={() => window.dispatchEvent(new CustomEvent('open-login'))} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:11, fontFamily:'var(--font)', textDecoration:'underline' }}>Login</button> to join the conversation.
                  </p>}
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <p style={{ textAlign:'center', color:'var(--gray-400)', padding:'40px 0' }}>No posts yet in this category.</p>
          )}
        </div>
      </div>

      {showAck && (
        <AcknowledgeModal
          type="post"
          onAcknowledge={handleConfirmedPost}
          onCancel={() => setShowAck(false)}
        />
      )}

      {reporting && (
        <ReportModal
          targetId={reporting.id}
          targetType="blog_post"
          targetName={reporting.name}
          onClose={() => setReporting(null)}
        />
      )}
    </div>
  )
}
